import { useCallback, useEffect, useState } from 'react';
import { subscribePush, unsubscribePush, type PushSubscriptionPayload } from '@/lib/api';

/**
 * usePushSubscription — Web Push subscribe / unsubscribe lifecycle.
 *
 * Reads the VAPID public key from `import.meta.env.VITE_VAPID_PUBLIC_KEY`
 * (must be set at build time). Without it, `subscribeUser()` throws a
 * descriptive error rather than silently no-op-ing.
 *
 * The hook mounts and probes for an existing subscription so the returned
 * `pushPermissionState` reflects reality across page reloads.
 *
 * NOTE: We do not request permission here. That is the job of
 * <PushPermissionPrompt />. This hook only flips bits AFTER the user opts in.
 */

export type PushPermissionState =
  | 'unsupported' // browser lacks Notification or PushManager
  | 'default' //  prompt not yet shown / answered
  | 'denied' //   user explicitly blocked
  | 'granted-unsubscribed' // permission granted but no live PushSubscription
  | 'granted-subscribed'; // permission granted AND live subscription exists

export interface UsePushSubscription {
  pushPermissionState: PushPermissionState;
  isBusy: boolean;
  error: Error | null;
  subscribeUser: () => Promise<void>;
  unsubscribeUser: () => Promise<void>;
  refresh: () => Promise<void>;
}

const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';

function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  // Allocate via ArrayBuffer explicitly so the resulting Uint8Array is
  // typed as Uint8Array<ArrayBuffer> (not <ArrayBufferLike>) — required for
  // PushSubscriptionOptions.applicationServerKey under strict TS lib.
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function toPayload(sub: PushSubscription): PushSubscriptionPayload {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint ?? sub.endpoint,
    expirationTime: json.expirationTime ?? sub.expirationTime ?? null,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
  };
}

async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

function computeState(
  perm: NotificationPermission | 'unsupported',
  sub: PushSubscription | null,
): PushPermissionState {
  if (perm === 'unsupported') return 'unsupported';
  if (perm === 'denied') return 'denied';
  if (perm === 'default') return 'default';
  return sub ? 'granted-subscribed' : 'granted-unsubscribed';
}

export function usePushSubscription(slug: string | undefined): UsePushSubscription {
  const [state, setState] = useState<PushPermissionState>(() =>
    isPushSupported() ? 'default' : 'unsupported',
  );
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (!isPushSupported()) {
      setState('unsupported');
      return;
    }
    try {
      const sub = await getCurrentSubscription();
      setState(computeState(Notification.permission, sub));
    } catch (err) {
      // Don't blow up the prompt — surface the error but assume default state.
      setState(computeState(Notification.permission, null));
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Self-heal: when permission is granted at the OS level, ALWAYS make sure
  // the current subscription is registered with the Worker. Three paths:
  //   1. granted-unsubscribed → call pushManager.subscribe() then POST
  //   2. granted-subscribed   → re-POST the existing OS-level subscription
  //                             (idempotent on the Worker side; KV upsert)
  //   3. anything else        → no-op
  // Path 2 fixes the "iOS holds an OS subscription that was never delivered
  // to the Worker" failure mode that bit us in the original test.
  useEffect(() => {
    if (!slug) return;
    if (state !== 'granted-unsubscribed' && state !== 'granted-subscribed') return;
    if (isBusy) return;
    let cancelled = false;
    void (async () => {
      try {
        if (!isPushSupported()) return;
        const reg = await navigator.serviceWorker.ready;
        // IMPORTANT: only re-POST an EXISTING subscription. Don't try to
        // call pushManager.subscribe() here — iOS Safari rejects subscribe
        // calls outside a user gesture, and we don't want to silently fail.
        // The subscribe() path lives in subscribeUser(), which fires from
        // the in-app prompt's tap handler.
        const sub = await reg.pushManager.getSubscription();
        if (!sub) return;
        if (cancelled) return;
        await subscribePush(slug, toPayload(sub));
        if (!cancelled) setState('granted-subscribed');
        console.log('[push] existing subscription re-synced to Worker');
      } catch (err) {
        if (!cancelled) {
          console.warn('[push] sync failed:', err);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, state, isBusy]);

  const subscribeUser = useCallback(async (): Promise<void> => {
    if (!slug) throw new Error('slug required to subscribe');
    if (!isPushSupported()) {
      throw new Error('Push notifications are not supported in this browser.');
    }
    if (!VAPID_PUBLIC_KEY) {
      throw new Error(
        'VITE_VAPID_PUBLIC_KEY is not configured. Push cannot be enabled until the build is rebuilt with the key.',
      );
    }

    setIsBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setState(perm === 'denied' ? 'denied' : 'default');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
        });
      }

      const payload = toPayload(sub);
      await subscribePush(slug, payload);
      setState('granted-subscribed');
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      // Re-probe so state reflects what actually happened.
      await refresh();
      throw e;
    } finally {
      setIsBusy(false);
    }
  }, [slug, refresh]);

  const unsubscribeUser = useCallback(async (): Promise<void> => {
    if (!slug) throw new Error('slug required to unsubscribe');
    if (!isPushSupported()) return;

    setIsBusy(true);
    setError(null);
    try {
      const sub = await getCurrentSubscription();
      if (!sub) {
        setState('granted-unsubscribed');
        return;
      }
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await unsubscribePush(slug, { endpoint });
      setState('granted-unsubscribed');
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      await refresh();
      throw e;
    } finally {
      setIsBusy(false);
    }
  }, [slug, refresh]);

  return { pushPermissionState: state, isBusy, error, subscribeUser, unsubscribeUser, refresh };
}
